import { NbfcLink, linkifyNbfc } from "../NbfcLink";
import React from "react";
import Footer from "../Footer";
import Navbar from "../Navbar";
import { Link } from "react-router-dom";

const PrivacyPolicy: React.FC = () => {
  const policySections = [
    {
      title: "1. Information We Collect",
      list: [
        "Personal Identification: Full Name, Email, Phone Number, Date of Birth, Address, PAN, Aadhaar, Employment Information, Bank Account Details, and Financial Data.",
        "Financial Information: Documents such as Bank Statements, Salary Slips, and Consent for Credit Checks are required for loan applications.",
        "Transactional Information: Details about your loans, repayment schedules, and transaction history are recorded.",
      ],
    },
    {
      title: "2. How We Use Your Information",
      list: [
        "Loan Processing: We assess your loan eligibility, process applications, and manage repayments.",
        "Service Improvement: We enhance our platform based on user feedback and interactions.",
        "Communication: We send notifications and respond to inquiries related to our services.",
        "Compliance: We comply with legal requirements like KYC and AML regulations.",
      ],
    },
    {
      title: "3. Sharing Your Information",
      list: [
        "Authorized Third Parties: We work with Credit Bureaus, Payment Gateways, and Verification Agencies to process and verify your information.",
        "Legal Authorities: We may disclose your information as required by the law, such as for regulatory reporting or fraud investigations.",
        `Parent Company: We may share your data with our parent company, ${import.meta.env.VITE_COMPANY_NBFC}, for loan processing.`,
      ],
    },
    {
      title: "4. Data Security",
      content:
        "We employ encryption, secure servers, and multi-factor authentication to safeguard your data. However, no system is 100% secure, and we encourage users to proceed with caution.",
    },
    {
      title: "5. Cookies and Tracking",
      content:
        "We use cookies and tracking technologies to enhance your browsing experience. You can control cookies through browser settings.",
    },
    {
      title: "6. Your Rights",
      content: (
        <>
          You have the right to access, correct, delete, or request portability of
          your data. You can also opt out of marketing communications at any time.
          <br />
          <br />
          For requesting deletion of your personal data, please refer to our{" "}
          <Link
            to="/account-delete-policy"
            className="text-(--primary) hover:underline font-medium"
          >
            User Data Deletion Policy
          </Link>
          .
        </>
      ),
    },

    {
      title: "7. Retention of Information",
      content:
        "We retain your information as long as it is necessary for business or legal reasons, and securely delete it when it is no longer needed.",
    },
    {
      title: "8. Third-Party Links",
      content:
        "Our platform may link to third-party websites. We are not responsible for their privacy practices.",
    },
    {
      title: "9. Children’s Privacy",
      content:
        "We do not knowingly collect data from children under 18. If we find any, we will delete it.",
    },
    {
      title: "10. Policy Updates",
      content:
        "We may update this policy periodically. Check this page for any changes.",
    },
    {
      title: "11. Contact Us",
      list: [
        `Email:${import.meta.env.VITE_COMPANY_EMAIL}`, ,
        `Phone: ${import.meta.env.VITE_COMPANY_PHONE}`,
        `Address: ${import.meta.env.VITE_COMPANY_ADDRESS}`,
      ],
    },
  ];

  return (
    <div className="min-w-screen bg-[var(--background-gradient-middle)]">
      <div className="max-w-[1440px] mx-auto flex flex-col gap-12 px-4 sm:px-6 lg:px-12 py-16">
        <Navbar />

        {/* Hero Section */}
        <div className="text-center mt-12 relative z-10 w-full mb-2">
          <h1 className="text-4xl md:text-7xl font-[900] leading-none tracking-tighter mb-4 bg-gradient-to-r from-[var(--primary)] to-[var(--secondary-accent)] text-transparent bg-clip-text">
            Privacy Policy
          </h1>

          <p className="text-[var(--text-gray)] font-bold text-lg md:text-xl max-w-3xl mx-auto leading-relaxed">
            At{" "}
            <span className="font-semibold text-[var(--primary)] text-xl">
              {import.meta.env.VITE_COMPANY_NAME}
            </span>
            , a lending platform under{" "}
            <span className="text-[var(--primary)] text-xl">
              <NbfcLink />
            </span>
            , we prioritize your privacy. This policy explains how we collect, use,
            and protect your data.
          </p>
        </div>

        {/* Policy Content */}
        <div className="max-w-4xl w-full mx-auto">
          <div className="bg-white/40 backdrop-blur-3xl rounded-[32px] md:rounded-[40px] border-2 border-white/50 shadow-[20px_20px_40px_rgba(0,0,0,0.04)] p-8 md:p-12 space-y-10">
            {policySections.map((section, idx) => (
              <div key={idx} className="space-y-4">
                <h2 className="text-2xl font-[900] text-[var(--text-primary)] tracking-tight mb-2 border-b-2 border-[var(--primary)]/20 pb-2">
                  {linkifyNbfc(section.title)}
                </h2>
                {section.content && (
                  <p className="text-[var(--text-gray)] text-base whitespace-pre-line leading-relaxed font-medium">
                    {linkifyNbfc(section.content)}
                  </p>
                )}
                {section.list && (
                  <ul className="list-disc list-outside ml-6 space-y-2 text-[var(--text-gray)] text-base font-medium">
                    {section.list.map((li, i) => (
                      <li key={i} className="pl-1">{linkifyNbfc(li)}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>

        <Footer />
      </div>
    </div>
  );
};

export default PrivacyPolicy;
